import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

import {
    formatTimeRange,
    getAvailabilityType,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_formatting';
import {
    type AvailabilityStatus,
    type RawTimeboxedAvailability,
    type ScheduleDayGroup,
    type ShiftScheduleSegment,
    type ShiftTimelineItem,
    type TimeboxedAvailabilityEntry,
    type TimeboxedAvailabilityEntrySegment,
    type TimeboxedAvailabilityItem,
    type TimeboxedAvailabilitySegment,
    type TimeboxedScheduleSegment,
    type TimelineItem,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_types';
import {
    getAvailabilitySourceLabel,
    getCollapsedSourceLabel,
    getTimeboxedAvailabilitySourcePriority,
} from '@apps/web/components/caregivers/caregiver_availability_source_labels';
import { shiftDateKey } from '@apps/web/lib/date_utils';

type TimeboxedDisplayAtom = {
    readonly status: AvailabilityStatus;
    readonly hasConflict: boolean;
    readonly startTimeMs: number;
    readonly endTimeMs: number;
    readonly startTime: string;
    readonly endTime: string;
    readonly entries: ReadonlyArray<TimeboxedAvailabilityEntry>;
    readonly effectiveSourceEntries: ReadonlyArray<TimeboxedAvailabilityEntry>;
};

type TimeboxedDisplayGroup = {
    readonly status: AvailabilityStatus;
    readonly hasConflict: boolean;
    readonly endTimeMs: number;
    readonly startTime: string;
    readonly endTime: string;
    readonly entries: Array<TimeboxedAvailabilityEntry>;
    readonly effectiveSourceEntries: Array<TimeboxedAvailabilityEntry>;
};

export function mergeTimeboxedAvailability(
    availability: ReadonlyArray<RawTimeboxedAvailability>,
    externalSourceLabel: string | undefined,
): Array<TimeboxedAvailabilityItem> {
    return mergeTimeboxedAvailabilityEntries(
        availability.map((item) => ({
            id: item.id,
            status: item.status,
            source: item.source,
            sourceLabel: getAvailabilitySourceLabel(
                item.source,
                item.source_system,
                externalSourceLabel,
            ),
            startTime: item.start_time,
            endTime: item.end_time,
        })),
    );
}

/**
 * Interval-merge availability entries into effective display windows.
 * Overlaps resolve by source priority; recurring (weekly-pattern) entries
 * always lose to dated entries, mirroring the recommendation engine's
 * one-time-overrides-weekly precedence.
 */
export function mergeTimeboxedAvailabilityEntries(
    unsortedEntries: ReadonlyArray<TimeboxedAvailabilityEntry>,
): Array<TimeboxedAvailabilityItem> {
    const entries = [...unsortedEntries].sort(
        (a, b) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime() ||
            new Date(a.endTime).getTime() - new Date(b.endTime).getTime(),
    );

    const boundaryLabels = new Map<number, string>();
    entries.forEach((entry) => {
        [entry.startTime, entry.endTime].forEach((value) => {
            const timeMs = new Date(value).getTime();
            if (!Number.isNaN(timeMs) && !boundaryLabels.has(timeMs)) {
                boundaryLabels.set(timeMs, value);
            }
        });
    });
    const boundaryTimes = Array.from(boundaryLabels.keys()).sort(
        (a, b) => a - b,
    );

    const groups: Array<TimeboxedDisplayGroup> = [];
    for (let index = 0; index < boundaryTimes.length - 1; index += 1) {
        const startTimeMs = boundaryTimes[index]!;
        const endTimeMs = boundaryTimes[index + 1]!;
        if (startTimeMs >= endTimeMs) {
            continue;
        }

        const coveringEntries = entries.filter(
            (entry) =>
                new Date(entry.startTime).getTime() < endTimeMs &&
                new Date(entry.endTime).getTime() > startTimeMs,
        );
        if (coveringEntries.length === 0) {
            continue;
        }

        const atom = buildTimeboxedDisplayAtom({
            coveringEntries,
            startTimeMs,
            endTimeMs,
            startTime:
                boundaryLabels.get(startTimeMs) ??
                new Date(startTimeMs).toISOString(),
            endTime:
                boundaryLabels.get(endTimeMs) ??
                new Date(endTimeMs).toISOString(),
        });
        const current = groups[groups.length - 1];
        if (current && canMergeTimeboxedDisplayAtom(current, atom)) {
            groups[groups.length - 1] = {
                ...current,
                endTimeMs: atom.endTimeMs,
                endTime: atom.endTime,
                entries: mergeTimeboxedEntryLists(
                    current.entries,
                    atom.entries,
                ),
                effectiveSourceEntries: mergeTimeboxedEntryLists(
                    current.effectiveSourceEntries,
                    atom.effectiveSourceEntries,
                ),
            };
            continue;
        }

        groups.push({
            status: atom.status,
            hasConflict: atom.hasConflict,
            endTimeMs: atom.endTimeMs,
            startTime: atom.startTime,
            endTime: atom.endTime,
            entries: [...atom.entries],
            effectiveSourceEntries: [...atom.effectiveSourceEntries],
        });
    }

    return groups.map((group) =>
        buildTimeboxedAvailabilityItem(
            group.entries,
            group.startTime,
            group.endTime,
            group.status,
            group.effectiveSourceEntries,
            group.hasConflict,
        ),
    );
}

/**
 * Locate an availability segment by id in freshly built day groups, so
 * details dialogs re-derive from live data instead of holding a snapshot;
 * returns null (closing the dialog) when a refetch removed the segment.
 */
export function findAvailabilitySegmentById(
    groups: ReadonlyArray<ScheduleDayGroup<TimeboxedScheduleSegment>>,
    segmentId: string | null,
): TimeboxedAvailabilitySegment | null {
    if (!segmentId) {
        return null;
    }
    for (const group of groups) {
        for (const segment of group.items) {
            if (segment.kind === 'availability' && segment.id === segmentId) {
                return segment;
            }
        }
    }
    return null;
}

/**
 * Orders day-keyed segments chronologically and groups them by day. Callers
 * decide how an interval becomes segments (sliced at local midnight, or kept
 * whole on its start day); this owns the ordering and group shape every
 * schedule grid relies on.
 */
export function groupSegmentsByDay<
    T extends {
        readonly dayKey: string;
        readonly dayLabel: string;
        readonly sortTimeMs: number;
    },
>(segments: ReadonlyArray<T>): Array<ScheduleDayGroup<T>> {
    const groups: Array<ScheduleDayGroup<T>> = [];
    const sorted = [...segments].sort((a, b) => a.sortTimeMs - b.sortTimeMs);
    sorted.forEach((segment) => {
        const current = groups[groups.length - 1];
        if (current?.key === segment.dayKey) {
            current.items.push(segment);
            return;
        }
        groups.push({
            key: segment.dayKey,
            label: segment.dayLabel,
            items: [segment],
        });
    });

    return groups;
}

export function buildTimeboxedDayGroups(
    items: ReadonlyArray<TimelineItem>,
    timezone: string,
): Array<ScheduleDayGroup<TimeboxedScheduleSegment>> {
    return groupSegmentsByDay(
        items.flatMap((item): Array<TimeboxedScheduleSegment> =>
            item.kind === 'shift'
                ? buildShiftSegments(item, timezone)
                : buildTimeboxedAvailabilitySegments(item, timezone),
        ),
    );
}

function buildTimeboxedDisplayAtom({
    coveringEntries,
    startTimeMs,
    endTimeMs,
    startTime,
    endTime,
}: {
    readonly coveringEntries: ReadonlyArray<TimeboxedAvailabilityEntry>;
    readonly startTimeMs: number;
    readonly endTimeMs: number;
    readonly startTime: string;
    readonly endTime: string;
}): TimeboxedDisplayAtom {
    const effectiveSourceEntries =
        getEffectiveTimeboxedSourceEntries(coveringEntries);
    return {
        status: getEffectiveTimeboxedStatus(effectiveSourceEntries),
        hasConflict: hasAvailabilityStatusConflict(
            getContentionEntries(coveringEntries),
        ),
        startTimeMs,
        endTimeMs,
        startTime,
        endTime,
        entries: coveringEntries,
        effectiveSourceEntries,
    };
}

function canMergeTimeboxedDisplayAtom(
    group: TimeboxedDisplayGroup,
    atom: TimeboxedDisplayAtom,
): boolean {
    return (
        group.status === atom.status &&
        group.hasConflict === atom.hasConflict &&
        group.endTimeMs === atom.startTimeMs
    );
}

function buildTimeboxedAvailabilityItem(
    entries: ReadonlyArray<TimeboxedAvailabilityEntry>,
    startTime: string,
    endTime: string,
    status: AvailabilityStatus,
    effectiveSourceEntries: ReadonlyArray<TimeboxedAvailabilityEntry>,
    hasConflict: boolean,
): TimeboxedAvailabilityItem {
    return {
        kind: 'availability',
        id: `availability-${entries.map((entry) => entry.id).join('-')}-${new Date(startTime).getTime()}-${new Date(endTime).getTime()}`,
        type: getAvailabilityType(status),
        status,
        sourceLabel: getCollapsedSourceLabel(effectiveSourceEntries),
        hasConflict,
        startTime,
        endTime,
        recurring: effectiveSourceEntries.every((entry) => entry.recurring),
        entries,
        effectiveSourceEntries,
    };
}

// Dated entries occupy priorities 0-2; recurring occurrences are pushed
// below all of them so a one-time exception always decides the window.
const RECURRING_SOURCE_PRIORITY_OFFSET = 3;

function getTimeboxedSourcePriority(entry: TimeboxedAvailabilityEntry): number {
    return (
        getTimeboxedAvailabilitySourcePriority(entry.source) +
        (entry.recurring ? RECURRING_SOURCE_PRIORITY_OFFSET : 0)
    );
}

// A dated entry overriding a recurring pattern is expected operation, not a
// source disagreement, so conflicts are computed within the layer that
// actually decides the window's status.
function getContentionEntries(
    entries: ReadonlyArray<TimeboxedAvailabilityEntry>,
): ReadonlyArray<TimeboxedAvailabilityEntry> {
    const datedEntries = entries.filter((entry) => !entry.recurring);
    return datedEntries.length > 0 ? datedEntries : entries;
}

function getEffectiveTimeboxedSourceEntries(
    entries: ReadonlyArray<TimeboxedAvailabilityEntry>,
): Array<TimeboxedAvailabilityEntry> {
    const bestPriority = Math.min(...entries.map(getTimeboxedSourcePriority));
    return entries.filter(
        (entry) => getTimeboxedSourcePriority(entry) === bestPriority,
    );
}

function getEffectiveTimeboxedStatus(
    entries: ReadonlyArray<TimeboxedAvailabilityEntry>,
): AvailabilityStatus {
    return entries.some((entry) => entry.status === 'unavailable')
        ? 'unavailable'
        : 'available';
}

function mergeTimeboxedEntryLists(
    existingEntries: ReadonlyArray<TimeboxedAvailabilityEntry>,
    nextEntries: ReadonlyArray<TimeboxedAvailabilityEntry>,
): Array<TimeboxedAvailabilityEntry> {
    const entryById = new Map<string, TimeboxedAvailabilityEntry>();
    [...existingEntries, ...nextEntries].forEach((entry) => {
        entryById.set(entry.id, entry);
    });
    return Array.from(entryById.values()).sort(
        (a, b) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime() ||
            new Date(a.endTime).getTime() - new Date(b.endTime).getTime(),
    );
}

function hasAvailabilityStatusConflict(
    entries: ReadonlyArray<{ readonly status: AvailabilityStatus }>,
): boolean {
    return new Set(entries.map((entry) => entry.status)).size > 1;
}

function getTimeboxedDaySlices(
    startTime: string,
    endTime: string,
    timezone: string,
) {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const slices: Array<{
        readonly dayKey: string;
        readonly dayLabel: string;
        readonly startTime: string;
        readonly endTime: string;
        readonly sortTimeMs: number;
    }> = [];

    if (end.getTime() <= start.getTime()) {
        return slices;
    }

    let dayKey = formatInTimeZone(startTime, timezone, 'yyyy-MM-dd');
    const endDayKey = formatInTimeZone(endTime, timezone, 'yyyy-MM-dd');

    while (dayKey <= endDayKey) {
        const nextDayKey = shiftDateKey(dayKey, 1);
        const dayStart = fromZonedTime(`${dayKey}T00:00:00`, timezone);
        const dayEnd = fromZonedTime(`${nextDayKey}T00:00:00`, timezone);
        const sliceStartMs = Math.max(start.getTime(), dayStart.getTime());
        const sliceEndMs = Math.min(end.getTime(), dayEnd.getTime());

        if (sliceStartMs < sliceEndMs) {
            const sliceStart = new Date(sliceStartMs).toISOString();
            const sliceEnd = new Date(sliceEndMs).toISOString();
            slices.push({
                dayKey,
                dayLabel: formatInTimeZone(
                    dayStart.toISOString(),
                    timezone,
                    'EEE, MMM d',
                ),
                startTime: sliceStart,
                endTime: sliceEnd,
                sortTimeMs: sliceStartMs,
            });
        }

        dayKey = nextDayKey;
    }

    return slices;
}

function getTimeboxedSegmentEntries(
    entries: ReadonlyArray<TimeboxedAvailabilityEntry>,
    startTime: string,
    endTime: string,
): Array<TimeboxedAvailabilityEntry> {
    const startMs = new Date(startTime).getTime();
    const endMs = new Date(endTime).getTime();
    return entries.filter(
        (entry) =>
            new Date(entry.startTime).getTime() < endMs &&
            new Date(entry.endTime).getTime() > startMs,
    );
}

function buildTimeboxedEntrySegments(
    entries: ReadonlyArray<TimeboxedAvailabilityEntry>,
    segmentStartTime: string,
    segmentEndTime: string,
    timezone: string,
): Array<TimeboxedAvailabilityEntrySegment> {
    const segmentStartMs = new Date(segmentStartTime).getTime();
    const segmentEndMs = new Date(segmentEndTime).getTime();

    return entries.map((entry) => {
        const startMs = Math.max(
            new Date(entry.startTime).getTime(),
            segmentStartMs,
        );
        const endMs = Math.min(new Date(entry.endTime).getTime(), segmentEndMs);
        const startTime = new Date(startMs).toISOString();
        const endTime = new Date(endMs).toISOString();

        return {
            id: `${entry.id}-${startMs}-${endMs}`,
            entry,
            status: entry.status,
            source: entry.source,
            sourceLabel: entry.sourceLabel,
            startTime,
            endTime,
            timeLabel: formatTimeRange(startTime, endTime, timezone),
        };
    });
}

function buildTimeboxedAvailabilitySegments(
    item: TimeboxedAvailabilityItem,
    timezone: string,
): Array<TimeboxedAvailabilitySegment> {
    return getTimeboxedDaySlices(item.startTime, item.endTime, timezone).map(
        (slice) => {
            const entries = getTimeboxedSegmentEntries(
                item.entries,
                slice.startTime,
                slice.endTime,
            );
            const effectiveSourceEntries = getTimeboxedSegmentEntries(
                item.effectiveSourceEntries,
                slice.startTime,
                slice.endTime,
            );
            return {
                kind: 'availability',
                id: `${item.id}-${slice.dayKey}-${slice.sortTimeMs}`,
                parentId: item.id,
                type: item.type,
                status: item.status,
                sourceLabel: getCollapsedSourceLabel(effectiveSourceEntries),
                hasConflict: hasAvailabilityStatusConflict(
                    getContentionEntries(entries),
                ),
                recurring: item.recurring,
                dayKey: slice.dayKey,
                dayLabel: slice.dayLabel,
                startTime: slice.startTime,
                endTime: slice.endTime,
                timeLabel: formatTimeRange(
                    slice.startTime,
                    slice.endTime,
                    timezone,
                ),
                sortTimeMs: slice.sortTimeMs,
                entries,
                entrySegments: buildTimeboxedEntrySegments(
                    entries,
                    slice.startTime,
                    slice.endTime,
                    timezone,
                ),
            };
        },
    );
}

function buildShiftSegments(
    item: ShiftTimelineItem,
    timezone: string,
): Array<ShiftScheduleSegment> {
    return getTimeboxedDaySlices(item.startTime, item.endTime, timezone).map(
        (slice) => ({
            kind: 'shift',
            id: `${item.id}-${slice.dayKey}-${slice.sortTimeMs}`,
            parentId: item.id,
            dayKey: slice.dayKey,
            dayLabel: slice.dayLabel,
            startTime: slice.startTime,
            endTime: slice.endTime,
            timeLabel: formatTimeRange(
                slice.startTime,
                slice.endTime,
                timezone,
            ),
            sortTimeMs: slice.sortTimeMs,
            details: item.details,
            open: item.open,
        }),
    );
}
